import client from './client';
import axios from 'axios';
import { getCookie, setCookie } from 'cookies-next';
import config from '../../config/configuration';
import { AppTypes, UserRefreshTokenWithCognitoResponseType } from '@zeus/shared';
import { endpoints } from './apiConfig';
import { cookieExpiryTime } from '../../config/constants';
import { store } from '../../app/store';

client.interceptors.request.use((config) => {
  // Modify the request config
  const AccessToken = getCookie('AccessToken') || sessionStorage.getItem('AccessToken');
  if (AccessToken) {
    config.headers['Authorization'] = `Bearer ${AccessToken}`;
  }
  const IdToken = getCookie('IdToken') || sessionStorage.getItem('IdToken');
  if (IdToken) {
    config.headers['x-id-token'] = IdToken;
  }
  config.headers['x-app-name'] = AppTypes.ONLINE;

  if (config.url !== endpoints.auth.setUserAttributesUrl) {
    const tenantId = getCookie('selectedBusinessId');
    if (tenantId) {
      config.headers['x-tenant-id'] = tenantId;
    }
  }

  return config;
});

const isSignedOut = () => {
  const auth = store.getState().auth;
  return !auth.isLogin || auth.signoutClicked;
};

// Shared refresh promise — all concurrent 401s attach to this instead of firing
// separate refresh POSTs. Cleared in .finally() so the next genuine 401 starts fresh.
let refreshPromise: Promise<{ accessToken: string; idToken: string }> | null = null;

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;
    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isSignedOut()) return Promise.reject(error);

      if (!refreshPromise) {
        const refreshToken = getCookie('RefreshToken') || sessionStorage.getItem('RefreshToken');
        const sub = getCookie('sub') || sessionStorage.getItem('sub');
        const headers = {
          Authorization: getCookie('AccessToken') || sessionStorage.getItem('AccessToken'),
          'x-id-token': getCookie('IdToken') || sessionStorage.getItem('IdToken'),
          'x-app-name': AppTypes.ONLINE,
          'x-tenant-id': getCookie('selectedBusinessId'),
        };

        refreshPromise = axios
          .post(`${config.BASE_URL}authentication/refresh-token`, { refreshToken, sub }, { headers })
          .then((res) => {
            // Throwing here rejects refreshPromise, cascading to all queued requests.
            if (isSignedOut()) throw error;
            if (res.status === 200) {
              const data: UserRefreshTokenWithCognitoResponseType = res.data;
              const accessToken = data.data?.AuthenticationResult?.AccessToken ?? '';
              const newRefreshToken = data.data?.AuthenticationResult?.RefreshToken ?? '';
              const IdToken = data.data?.AuthenticationResult?.IdToken ?? '';
              const cookieOptions = { maxAge: cookieExpiryTime };
              setCookie('AccessToken', accessToken, cookieOptions);
              setCookie('IdToken', IdToken, cookieOptions);
              if (newRefreshToken) {
                setCookie('RefreshToken', newRefreshToken, cookieOptions);
              }
              axios.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;
              return { accessToken, idToken: IdToken };
            }
            throw error;
          })
          .catch(() => {
            // Always reject with the original 401 error so callers see a consistent
            // error shape regardless of whether the refresh POST itself failed.
            throw error;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      if (isSignedOut()) return Promise.reject(error);

      const pending = refreshPromise;
      return pending.then(({ accessToken, idToken }) => {
        originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
        originalRequest.headers['x-id-token'] = idToken;
        return axios(originalRequest);
      });
    }
    return Promise.reject(error);
  }
);
export default client;
